import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Inbox, UserPlus, Trash2, Mail, ChevronDown, ChevronRight, CheckCircle2, PhoneCall, Flame, Leaf, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ProductCheckboxes from "@/components/ProductCheckboxes";
import { AccountPicker } from "@/components/AccountPicker";

/**
 * HubSpot inbound leads queue — two stages, both inside the queue:
 *   1. New — unassigned (no owner) → Assign to an owner, or Discard.
 *   2. Assigned — pre-call (has owner, not yet qualified) → Qualify (MQL), Reassign, Discard.
 * Only qualifying moves a lead OUT to the Active pipeline.
 */
export default function InboundLeadsQueue({ users = [], meta = {}, onChange, countHint = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [assigning, setAssigning] = useState(null); // lead being (re)assigned
  const [qualifying, setQualifying] = useState(null); // lead being qualified
  const [tempFilter, setTempFilter] = useState("all"); // all | hot | nurture | untagged
  const [busyTempId, setBusyTempId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads/inbound-queue");
      setItems(r.data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [countHint]);

  const filtered = useMemo(() => {
    if (tempFilter === "all") return items;
    if (tempFilter === "untagged") return items.filter((l) => !l.hubspot_temp);
    return items.filter((l) => (l.hubspot_temp || "") === tempFilter);
  }, [items, tempFilter]);
  const unassigned = useMemo(() => filtered.filter((l) => !l.owner_id), [filtered]);
  const assigned = useMemo(() => filtered.filter((l) => !!l.owner_id), [filtered]);
  const tempCounts = useMemo(() => ({
    all: items.length,
    hot: items.filter((l) => l.hubspot_temp === "hot").length,
    nurture: items.filter((l) => l.hubspot_temp === "nurture").length,
    untagged: items.filter((l) => !l.hubspot_temp).length,
  }), [items]);

  const setTemp = async (leadId, next) => {
    setBusyTempId(leadId);
    // Optimistic UI
    setItems((prev) => prev.map((l) => l.id === leadId ? { ...l, hubspot_temp: next } : l));
    try {
      await api.post(`/leads/${leadId}/hubspot-temp`, { hubspot_temp: next });
      toast.success(next ? `Marked ${next.toUpperCase()}` : "Tag cleared");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update tag");
      await load();
    } finally {
      setBusyTempId(null);
    }
  };

  const handleDiscard = async (id) => {
    if (!window.confirm("Discard this inbound lead? It will be marked as discarded (not deleted).")) return;
    try {
      await api.post(`/leads/${id}/discard`);
      toast.success("Lead discarded");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to discard");
    }
  };

  const handleReparse = async (id) => {
    try {
      await api.post(`/leads/${id}/reparse`);
      toast.success("Lead re-parsed from the stored HubSpot email");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reparse failed");
    }
  };

  const afterMutation = async () => {
    setAssigning(null);
    setQualifying(null);
    await load();
    onChange?.();
  };

  const renderRows = (list, stage) => {
    if (list.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-4 py-4 text-center text-xs text-slate-400">
            {stage === "new" ? "Nothing waiting to be assigned." : "No leads currently being worked pre-call."}
          </td>
        </tr>
      );
    }
    return list.map((l) => {
      const isOpen = !!expanded[l.id];
      return (
        <React.Fragment key={l.id}>
          <tr className="border-b border-blue-100 hover:bg-white" data-testid={`inbound-row-${l.id}`}>
            <td className="px-3 py-3 align-top">
              <button
                onClick={() => setExpanded((prev) => ({ ...prev, [l.id]: !prev[l.id] }))}
                className="text-slate-500 hover:text-slate-800"
                aria-label="Expand"
              >
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </td>
            <td className="px-3 py-3">
              <div className="font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                <span>
                  {(l.first_name || "").trim()} {(l.last_name || "").trim() || <span className="text-slate-400 italic">(no name)</span>}
                </span>
                {l.hubspot_temp === "hot" && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 border border-rose-200 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700 uppercase" data-testid={`inbound-temp-badge-${l.id}`}>
                    <Flame className="h-2.5 w-2.5" /> Hot
                  </span>
                )}
                {l.hubspot_temp === "nurture" && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 uppercase" data-testid={`inbound-temp-badge-${l.id}`}>
                    <Leaf className="h-2.5 w-2.5" /> Nurture
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">{l.company || "—"} · {l.job_title || "—"}</div>
              {stage === "assigned" && l.owner_name && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-800" data-testid={`inbound-owner-${l.id}`}>
                  <PhoneCall className="h-2.5 w-2.5" /> {l.owner_name} · pre-call
                </div>
              )}
            </td>
            <td className="px-3 py-3 text-xs">
              {l.email ? (
                <a href={`mailto:${l.email}`} className="text-blue-700 hover:underline flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {l.email}
                </a>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </td>
            <td className="px-3 py-3 text-xs text-slate-700">
              <div className="space-y-0.5" data-testid={`inbound-meta-${l.id}`}>
                <MetaRow label="Source" value={l.lead_source} />
                <MetaRow label="Sub-cat" value={l.lead_source_sub_cat} />
                <MetaRow label="Campaign" value={l.inbound_campaign} />
                <MetaRow label="Status" value={l.inbound_lead_status} status />
              </div>
              {l.inbound_form_name && (
                <div className="text-[10px] text-slate-400 mt-1 truncate max-w-[220px]" title={l.inbound_form_name}>
                  Form: {l.inbound_form_name}
                </div>
              )}
            </td>
            <td className="px-3 py-3 text-xs text-slate-500 font-mono">
              {fmtDate(l.inbound_received_at || l.created_at)}
            </td>
            <td className="px-3 py-3 align-top">
              <div className="flex items-center gap-1 justify-end flex-wrap">
                {/* HOT/NURTURE quick-set — toggles current value on click */}
                <button
                  type="button"
                  disabled={busyTempId === l.id}
                  onClick={() => setTemp(l.id, l.hubspot_temp === "hot" ? null : "hot")}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase transition-colors ${l.hubspot_temp === "hot" ? "bg-rose-100 border-rose-300 text-rose-800" : "bg-white border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-700"}`}
                  data-testid={`inbound-mark-hot-${l.id}`}
                  title={l.hubspot_temp === "hot" ? "Remove HOT tag" : "Mark as HOT — call urgently"}
                >
                  <Flame className="h-3 w-3" /> Hot
                </button>
                <button
                  type="button"
                  disabled={busyTempId === l.id}
                  onClick={() => setTemp(l.id, l.hubspot_temp === "nurture" ? null : "nurture")}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase transition-colors ${l.hubspot_temp === "nurture" ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-700"}`}
                  data-testid={`inbound-mark-nurture-${l.id}`}
                  title={l.hubspot_temp === "nurture" ? "Remove NURTURE tag" : "Mark as NURTURE — long-tail follow-up"}
                >
                  <Leaf className="h-3 w-3" /> Nurture
                </button>
                {stage === "assigned" && (
                  <Button
                    size="sm"
                    onClick={() => setQualifying(l)}
                    data-testid={`qualify-${l.id}`}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Qualify (MQL)
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAssigning(l)}
                  data-testid={`assign-${l.id}`}
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                  {stage === "assigned" ? "Reassign" : "Assign"}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleReparse(l.id)}
                  data-testid={`reparse-${l.id}`}
                  title="Re-parse from HubSpot email — fills in Campaign / Status / HOT·NURTURE from the stored notification body"
                >
                  <RefreshCw className="h-3.5 w-3.5 text-slate-500" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDiscard(l.id)}
                  data-testid={`discard-${l.id}`}
                  title="Discard"
                >
                  <Trash2 className="h-3.5 w-3.5 text-slate-500" />
                </Button>
              </div>
            </td>
          </tr>
          {isOpen && (
            <tr className="bg-white border-b border-blue-100">
              <td></td>
              <td colSpan={5} className="px-3 py-3">
                <div className="grid sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="font-mono uppercase text-[10px] text-slate-400 mb-1">HubSpot metadata</div>
                    <div><b>Form:</b> {l.inbound_form_name || "—"}</div>
                    <div><b>Page:</b> {l.inbound_page || "—"}</div>
                    <div><b>Lead source:</b> {l.lead_source || "—"}</div>
                    <div><b>Sub-category:</b> {l.lead_source_sub_cat || "—"}</div>
                    <div><b>Campaign:</b> {l.inbound_campaign || "—"}</div>
                    <div><b>Lead status (HubSpot):</b> {l.inbound_lead_status || "—"}</div>
                    <div><b>Received from:</b> {l.inbound_from || "—"}</div>
                    <div><b>Users:</b> {l.number_of_users ?? "—"}</div>
                    {(l.interested_products || []).length > 0 && (
                      <div><b>Products:</b> {(l.interested_products || []).join(", ")}</div>
                    )}
                  </div>
                  <div>
                    <div className="font-mono uppercase text-[10px] text-slate-400 mb-1">Raw email snippet</div>
                    <pre className="whitespace-pre-wrap text-[11px] leading-snug text-slate-700 bg-slate-50 p-2 rounded border border-slate-200 max-h-40 overflow-y-auto">
                      {l.raw_email_snippet || "(empty)"}
                    </pre>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  const Section = ({ title, hint, count, accent, stage, list }) => (
    <div>
      <div className={`flex items-center gap-2 px-3 py-2 border-b border-blue-100 ${accent}`}>
        <span className="text-[11px] font-semibold uppercase tracking-wider">{title}</span>
        <Badge variant="outline" className="bg-white text-slate-700 border-slate-300" data-testid={`inbound-count-${stage}`}>{count}</Badge>
        <span className="text-[11px] text-slate-500 font-normal normal-case">{hint}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-blue-100/40 border-b border-blue-100 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2 w-6"></th>
            <th className="px-3 py-2">Contact</th>
            <th className="px-3 py-2">Business email</th>
            <th className="px-3 py-2">Source / campaign / status</th>
            <th className="px-3 py-2">Received</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody data-testid={`inbound-queue-body-${stage}`}>{renderRows(list, stage)}</tbody>
      </table>
    </div>
  );

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="inbound-leads-queue">
      <CardHeader className="border-b border-blue-100 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Inbox className="h-4 w-4 text-blue-600" />
            HubSpot inbound queue
            <Badge variant="outline" className="ml-1 bg-white text-blue-800 border-blue-300" data-testid="inbound-queue-count">
              {items.length}
            </Badge>
          </CardTitle>
          <div className="text-xs text-slate-500 font-mono">
            New leads land here → assign an owner → owner calls & qualifies to MQL → moves to Active.
          </div>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="inbound-temp-filters">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mr-1">Filter</span>
            {[
              { v: "all", l: "All", cls: "bg-white border-slate-200 text-slate-700", active: "bg-slate-900 border-slate-900 text-white" },
              { v: "hot", l: "Hot", icon: Flame, cls: "bg-white border-slate-200 text-slate-700", active: "bg-rose-100 border-rose-300 text-rose-800" },
              { v: "nurture", l: "Nurture", icon: Leaf, cls: "bg-white border-slate-200 text-slate-700", active: "bg-emerald-100 border-emerald-300 text-emerald-800" },
              { v: "untagged", l: "Untagged", cls: "bg-white border-slate-200 text-slate-500", active: "bg-slate-100 border-slate-300 text-slate-800" },
            ].map((f) => {
              const isOn = tempFilter === f.v;
              const Icon = f.icon;
              return (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => setTempFilter(f.v)}
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${isOn ? f.active : f.cls}`}
                  data-testid={`inbound-filter-${f.v}`}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  {f.l}
                  <span className="font-mono text-[10px] opacity-70">({tempCounts[f.v]})</span>
                </button>
              );
            })}
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {items.length === 0 && !loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            <div className="flex flex-col items-center gap-2">
              <Inbox className="h-6 w-6 text-blue-300" />
              <div className="font-medium text-slate-700">No inbound leads yet</div>
              <div className="text-xs text-slate-500 max-w-lg">
                When HubSpot forwards a new form-submission email to <b>leads@xentra.co.uk</b>, it will appear
                here automatically for triage.
              </div>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-blue-200">
            <Section
              title="New — unassigned"
              hint="Just arrived · assign an owner to call"
              count={unassigned.length}
              accent="bg-amber-50 text-amber-800"
              stage="new"
              list={unassigned}
            />
            <Section
              title="Assigned — pre-call"
              hint="Owner working the lead · qualify once it's an MQL"
              count={assigned.length}
              accent="bg-emerald-50 text-emerald-800"
              stage="assigned"
              list={assigned}
            />
          </div>
        )}
      </CardContent>

      {assigning && (
        <AssignDialog
          lead={assigning}
          users={users}
          meta={meta}
          onClose={() => setAssigning(null)}
          onDone={afterMutation}
        />
      )}
      {qualifying && (
        <QualifyDialog
          lead={qualifying}
          users={users}
          meta={meta}
          onClose={() => setQualifying(null)}
          onDone={afterMutation}
        />
      )}
    </Card>
  );
}

function AssignDialog({ lead, users, meta, onClose, onDone }) {
  const staffUsers = useMemo(() => (users || []).filter((u) => u.user_id && u.user_id !== "all"), [users]);
  const [ownerId, setOwnerId] = useState(lead.owner_id || staffUsers[0]?.user_id || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!ownerId) { toast.error("Pick an owner"); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/assign`, {
        owner_id: ownerId,
        notes: notes || undefined,
      });
      toast.success(`Assigned to ${staffUsers.find((u) => u.user_id === ownerId)?.name || "owner"} — stays in the queue`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="assign-inbound-dialog">
        <DialogHeader>
          <DialogTitle>Assign inbound lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <LeadSummaryCard lead={lead} />
          <div className="rounded border border-amber-200 bg-amber-50/60 p-2.5 text-[11px] text-amber-900">
            This assigns an owner to call the lead. It <b>stays in the queue</b> under <b>Assigned — pre-call</b>.
            The owner qualifies it to MQL after the call.
          </div>
          <Field label="Owner (who will call)">
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger data-testid="assign-owner-select"><SelectValue placeholder="Choose owner" /></SelectTrigger>
              <SelectContent>
                {staffUsers.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Add notes (optional)">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. High priority — inbound demo request" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="assign-submit">
            {saving ? "Assigning…" : "Assign to owner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QualifyDialog({ lead, users, meta, onClose, onDone }) {
  const staffUsers = useMemo(() => (users || []).filter((u) => u.user_id && u.user_id !== "all"), [users]);
  const [f, setF] = useState({
    owner_id: lead.owner_id || staffUsers[0]?.user_id || "",
    first_name: lead.first_name || "",
    last_name: lead.last_name || "",
    company: lead.company || "",
    job_title: lead.job_title || "",
    email: lead.email || "",
    phone: lead.phone || "",
    number_of_users: lead.number_of_users ?? "",
    industry: lead.industry || "",
    next_action: "",
    next_action_date: "",
    notes: "",
  });
  const [products, setProducts] = useState(lead.interested_products || []);
  const [linkedAccountId, setLinkedAccountId] = useState(lead.linked_account_id || "");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.owner_id) { toast.error("Pick an owner"); return; }
    if (!f.next_action) { toast.error("A next action is required to qualify"); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/promote`, {
        owner_id: f.owner_id,
        status: "MQL",
        industry: f.industry || undefined,
        number_of_users: f.number_of_users === "" ? undefined : Number(f.number_of_users),
        next_action: f.next_action,
        next_action_date: f.next_action_date || undefined,
        notes: f.notes || undefined,
        first_name: f.first_name || undefined,
        last_name: f.last_name || undefined,
        company: f.company || undefined,
        job_title: f.job_title || undefined,
        email: f.email || undefined,
        phone: f.phone || undefined,
        interested_products: products,
        linked_account_id: linkedAccountId || "",
      });
      toast.success("Qualified as MQL — moved to Active pipeline");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to qualify");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qualify-inbound-dialog">
        <DialogHeader>
          <DialogTitle>Qualify lead as MQL</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-blue-200 bg-blue-50/60 p-2.5 text-[11px] text-blue-900">
            Confirm the contact's details and the STAX products they're interested in, set a required next action,
            then this lead moves to <b>Active</b> as an <b>MQL</b>.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="First" data-testid="qualify-first" />
            </Field>
            <Field label="Last name">
              <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Last" data-testid="qualify-last" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Organisation">
              <Input value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="qualify-company" />
            </Field>
            <Field label="Job title">
              <Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} />
            </Field>
          </div>
          <Field label="Link to existing account (optional)">
            <AccountPicker
              value={linkedAccountId}
              onChange={(id, name) => { setLinkedAccountId(id); if (name) set("company", name); }}
              testid="qualify-account-picker"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              {linkedAccountId ? "Linked to a current account — organisation set from it." : "Leave empty if this is a new prospect."}
            </p>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="qualify-email" />
            </Field>
            <Field label="Phone">
              <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="qualify-phone" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="User count">
              <Input type="number" value={f.number_of_users} onChange={(e) => set("number_of_users", e.target.value)} data-testid="qualify-users" />
            </Field>
            <Field label="Owner">
              <Select value={f.owner_id} onValueChange={(v) => set("owner_id", v)}>
                <SelectTrigger data-testid="qualify-owner-select"><SelectValue placeholder="Choose owner" /></SelectTrigger>
                <SelectContent>
                  {staffUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="STAX products of interest">
            <ProductCheckboxes products={meta?.products || []} selected={products} onChange={setProducts} testidPrefix="qualify-stax" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Next action *">
              <Select value={f.next_action || "none"} onValueChange={(v) => set("next_action", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="qualify-next-action"><SelectValue placeholder="Required" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Choose —</SelectItem>
                  {(meta?.next_actions || []).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Next action date">
              <Input type="date" value={f.next_action_date} onChange={(e) => set("next_action_date", e.target.value)} />
            </Field>
          </div>

          <Field label="Add notes (appended)">
            <Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. Spoke to prospect — keen on Cyber STAX, budget confirmed" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="qualify-submit">
            {saving ? "Qualifying…" : "Qualify & move to Active"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadSummaryCard({ lead }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs">
      <div className="font-medium text-slate-900 mb-0.5">{lead.first_name} {lead.last_name}</div>
      <div className="text-slate-600">{lead.company || "—"}</div>
      <div className="text-blue-700 mt-1">{lead.email || "—"}</div>
      <div className="text-slate-500 mt-1">Form: {lead.inbound_form_name || "—"} · {lead.inbound_campaign || "—"}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function MetaRow({ label, value, status = false }) {
  const v = value && String(value).trim();
  const statusStyle = status && v
    ? (/hot/i.test(v)
        ? "bg-rose-100 border-rose-200 text-rose-800"
        : /nurture/i.test(v)
        ? "bg-emerald-100 border-emerald-200 text-emerald-800"
        : "bg-slate-100 border-slate-200 text-slate-700")
    : "";
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 min-w-[62px] flex-shrink-0">
        {label}
      </span>
      {status && v ? (
        <span className={`inline-flex items-center rounded border px-1.5 py-[1px] text-[10px] font-semibold ${statusStyle}`}>
          {v}
        </span>
      ) : (
        <span className="text-slate-700 truncate" title={v || ""}>{v || "—"}</span>
      )}
    </div>
  );
}
